import { Headline } from '@oneaudi/unified-web-components';
import type { FC } from 'react';
import React from 'react';
import styled from 'styled-components';
import type { CarlineConsumptionAndEmissionSummary } from '../../graphql/documents/carline-structure.generated';
import { useContent } from '../context/ContentContext';
import { useTrackingManager } from '../hooks/useTrackingManager';
import Actions from './Actions';
import { Badges } from './Badges';
import { CardImage } from './CardImage';
import ConsumptionAndEmission from './ConsumptionAndEmission';
import { Price } from './Price';
import type { Carline } from '../utils/filter';
import { getConfigureUrl } from '../utils/getConfigureLink';

export interface CardProps {
  readonly index: number;
  readonly consumptionAndEmission?: CarlineConsumptionAndEmissionSummary;
  readonly carline: Carline;
}

export const Card: FC<CardProps> = ({ index, carline }) => {
  const {
    identifier: { id: carlineId },
    name: carlineName,
    prices: { minPrice },
    availableStock,
    fuelTypes,
    group,
    markAsNew,
    isFake,
    defaultConfiguredCar,
    exteriorViews,
    links,
    consumptionAndEmission,
  } = carline;
  const configureLink = getConfigureUrl(carline);
  const ngcImageUrl = defaultConfiguredCar?.media?.images?.[0]?.url ?? '';
  const price = !isFake ? minPrice?.value : undefined;
  const {
    caeDisplayOption: { showCae },
  } = useContent();

  const { clickCardImage } = useTrackingManager();

  return (
    <CardWrapper data-carlineid={carlineId} data-testid={`carCard-${carlineId}`}>
      <ImageWrapper backDropNumber="1" onClick={() => clickCardImage(group, carlineName)}>
        <Badges fuelTypes={fuelTypes} markAsNew={markAsNew} />
        <CardImage
          index={index}
          alt={carlineName}
          fallbackSrc={exteriorViews?.beautyFront}
          ngcImageUrl={ngcImageUrl}
        />
      </ImageWrapper>
      <ContentWrapper>
        <Title
          variant={{
            type: 'headline',
            order: '4',
            weight: 'normal',
          }}
          className="carlineTitle"
          tag="h4"
        >
          {carlineName}
        </Title>
        <Price value={price} />
        <Actions
          title={carlineName}
          discoverLink={links?.carlineStartPage?.url || undefined}
          configureLink={configureLink || undefined}
          availableStock={availableStock || undefined}
          group={group}
          carline={{
            id: carlineId,
            name: carlineName,
            group,
          }}
        />
        {consumptionAndEmission?.summary && showCae && (
          <DisclaimerWrapper>
            <ConsumptionAndEmission
              consumptionData={consumptionAndEmission.summary}
              carlineName={carlineName}
            />
          </DisclaimerWrapper>
        )}
      </ContentWrapper>
    </CardWrapper>
  );
};

const CardWrapper = styled.div`
  overflow: hidden;
  height: calc(100% - var(--spacing-relative-lg));
  background-color: ${({ theme }) => theme.colors.colorCanvasDefault};
  display: flex;
  flex-direction: column;

  &:last-child {
    padding-bottom: var(--spacing-relative-lg);
  }
`;

const ImageWrapper = styled.div<{ backDropNumber: string }>`
  position: relative;
  aspect-ratio: 4 / 3;
  font-size: 0;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: var(--spacing-relative-lg) var(--spacing-relative-lg) 0;
`;

const Title = styled(Headline)`
  text-wrap: pretty;
`;

const DisclaimerWrapper = styled.div`
  position: relative;
  padding-top: var(--spacing-relative-lg);
  background-color: ${({ theme }) => theme.colors.colorCanvasDefault};
  margin-top: auto;
`;
